import { Component, OnInit } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { BehaviorSubject } from 'rxjs';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { DialogComponent } from 'app/shared/dialogs/dialog.component';
import { MailDialogService } from 'app/shared/dialogs/mail/mail-dialog.service';

interface Attachment {
  name: string;
  path: string;
  checked: boolean;
}

@Component({
  selector: 'app-send-electronic-invoice',
  templateUrl: './send-electronic-invoice.component.html',
})
export class SendElectronicInvoiceComponent
  extends DialogComponent
  implements OnInit
{
  public attachments = new BehaviorSubject<Attachment[]>([]);
  public mergePDFs = true;
  public previewIsLoading = true;
  public previewUrl: SafeUrl;
  public invoiceId: number | null = null;
  public isLoading = false;

  constructor(
    public dialogRef: DynamicDialogRef,
    public dialogConfig: DynamicDialogConfig,
    private domSanitizer: DomSanitizer,
    private mailDialogService: MailDialogService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.dialogConfig.header = 'Skicka e-faktura';
    const dialogInvoiceId = this.dialogConfig.data?.invoiceId;
    this.invoiceId =
      dialogInvoiceId !== undefined && dialogInvoiceId !== null
        ? Number(dialogInvoiceId)
        : null;
    if (this.invoiceId) {
      this.fetchFormData();
    } else {
      this.previewIsLoading = false;
    }
  }

  private fetchFormData(): void {
    const url = this.buildFormUrl();

    this.httpService
      .makeHttpPostRequest(url, { files: this.dialogConfig.data?.files })
      .subscribe(response => {
        if (response.status !== 'success') {
          this.messageService.insertData({
            summary: response.msg,
            severity: 'error',
          });
          this.previewIsLoading = false;
          return;
        }

        this.handleAttachments(response.attachments);
        this.refreshPreview();
      });
  }

  private buildFormUrl(): string {
    const query = new URLSearchParams();
    if (this.invoiceId !== null) {
      query.append('invoiceId', String(this.invoiceId));
    }
    return `/company/HyperionMailForm?${query.toString()}`;
  }

  private handleAttachments(attachmentsParam): void {
    const attachmentList: Attachment[] = [];
    let i = 0;
    for (const path in attachmentsParam) {
      attachmentList.push({
        name: attachmentsParam[path],
        path,
        checked: i++ === 0,
      });
    }
    this.attachments.next(attachmentList);
  }

  public toggleAttachment(attachment: Attachment): void {
    attachment.checked = !attachment.checked;
    this.refreshPreview();
  }

  public onMergeChange(): void {
    this.refreshPreview();
  }

  public refreshPreview(): void {
    this.previewIsLoading = true;
    const selectedAttachments = this.attachments.value
      .filter(att => att.checked)
      .map(att => `attachments[]=${att.path}`);

    const params = [...selectedAttachments];
    params.push('mergePDFs=1');

    const baseUrl = '/company/MailPdfPreview';
    const url = params.length > 0 ? `${baseUrl}?${params.join('&')}` : baseUrl;

    this.previewUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(url);
  }

  public onPreviewLoaded(): void {
    this.previewIsLoading = false;
  }

  public closeAction(): void {
    this.dialogRef.close();
  }

  public send(): void {
    this.isLoading = true;

    const selectedAttachments = this.attachments.value
      .filter(att => att.checked)
      .map(att => att.path);

    this.httpService
      .makeHttpPostRequest(
        `/company/CrediflowSend?invoiceId=${this.invoiceId}`,
        {
          Mail: {
            attachments: selectedAttachments,
          },
        }
      )
      .subscribe({
        next: response => {
          this.isLoading = false;
          if (response.status === 'success') {
            this.messageService.insertData({
              summary: 'E-faktura skickad',
              severity: 'success',
            });
            this.dialogRef.close(true);
          } else {
            this.messageService.insertData({
              summary: response.msg,
              severity: 'error',
            });
          }
        },
        error: () => {
          this.isLoading = false;
          this.messageService.insertData({
            summary: 'Ett fel uppstod vid skickandet av e-fakturan',
            severity: 'error',
          });
        },
      });
  }

  public openMailDialog(): void {
    if (!this.invoiceId) {
      return;
    }

    this.dialogRef.close();
    this.mailDialogService.openMailDialog({ invoiceId: this.invoiceId });
  }
}
